import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiStatics } from "../../Utils/constants";

const baseUrl = () =>
  `${process.env.REACT_APP_API_BACKEND_URL}/${ApiStatics.notifications.notifications}`;

const authConfig = () => ({
  headers: {
    Authorization: "Bearer " + localStorage.getItem("token"),
  },
});

// * The other slices route this through expireTokenHandler, which needs a
// * navigate passed down from a component. These thunks are called from a
// * background poll with no navigate to hand, so they end the session the same
// * way AuthSlice.clearUserSession does. * //
const handleAuthError = (error) => {
  if (error?.response?.status === 401) {
    localStorage.clear();
    window.location.href = "/";
  }
  throw error;
};

export const getNotifications = createAsyncThunk(
  `Notification/getNotifications`,
  async ({ page = 1 } = {}) => {
    let response;
    try {
      response = await axios.get(
        `${baseUrl()}?page=${page}&limit=20`,
        authConfig()
      );
    } catch (error) {
      return handleAuthError(error);
    }
    return {
      data: response.data.data.data,
      total: response.data.total,
      unreadCount: response.data.unreadCount,
      page,
    };
  }
);

// * Polled for the badge, so it stays separate from the list fetch. * //
export const getUnreadCount = createAsyncThunk(
  `Notification/getUnreadCount`,
  async () => {
    try {
      const response = await axios.get(
        `${baseUrl()}/${ApiStatics.notifications.unreadCount}`,
        authConfig()
      );
      return response.data.data.unreadCount;
    } catch (error) {
      return handleAuthError(error);
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  `Notification/markRead`,
  async ({ id }) => {
    try {
      await axios.patch(
        `${baseUrl()}/${id}/${ApiStatics.notifications.read}`,
        {},
        authConfig()
      );
      return id;
    } catch (error) {
      return handleAuthError(error);
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  `Notification/markAllRead`,
  async () => {
    try {
      await axios.post(
        `${baseUrl()}/${ApiStatics.notifications.readAll}`,
        {},
        authConfig()
      );
      return true;
    } catch (error) {
      return handleAuthError(error);
    }
  }
);

export const getNotificationPreferences = createAsyncThunk(
  `Notification/getPreferences`,
  async () => {
    try {
      const response = await axios.get(
        `${baseUrl()}/${ApiStatics.notifications.preferences}`,
        authConfig()
      );
      return response.data.data.data;
    } catch (error) {
      return handleAuthError(error);
    }
  }
);

export const updateNotificationPreference = createAsyncThunk(
  `Notification/updatePreferences`,
  async ({ type, channel, enabled }) => {
    const payload = { [type]: { [channel]: enabled } };
    try {
      await axios.patch(
        `${baseUrl()}/${ApiStatics.notifications.preferences}`,
        payload,
        authConfig()
      );
      return { type, channel, enabled };
    } catch (error) {
      return handleAuthError(error);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  loading: false,
  open: false,
  preferences: {},
  preferencesLoading: false,
};

const NotificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    openNotifications(state) {
      state.open = true;
    },
    closeNotifications(state) {
      state.open = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNotifications.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.notifications =
          payload.page === 1
            ? payload.data
            : [...state.notifications, ...payload.data];
        state.total = payload.total;
        state.unreadCount = payload.unreadCount;
        state.page = payload.page;
      })
      .addCase(getNotifications.rejected, (state) => {
        state.loading = false;
      });

    builder.addCase(getUnreadCount.fulfilled, (state, { payload }) => {
      state.unreadCount = payload;
    });

    builder.addCase(markNotificationRead.fulfilled, (state, { payload }) => {
      const target = state.notifications.find((item) => item.id === payload);
      if (target && !target.read) {
        target.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });

    builder
      .addCase(getNotificationPreferences.pending, (state) => {
        state.preferencesLoading = true;
      })
      .addCase(getNotificationPreferences.fulfilled, (state, { payload }) => {
        state.preferencesLoading = false;
        state.preferences = payload;
      })
      .addCase(getNotificationPreferences.rejected, (state) => {
        state.preferencesLoading = false;
      });

    // * Applied optimistically so the switch does not lag the tap. * //
    builder.addCase(
      updateNotificationPreference.pending,
      (state, { meta }) => {
        const { type, channel, enabled } = meta.arg;
        state.preferences[type] = {
          ...(state.preferences[type] || {}),
          [channel]: enabled,
        };
      }
    );

    // * The toggle is flipped on pending so it does not lag the tap, so a
// * failed save has to put it back - otherwise the user is looking at a
// * setting the server never accepted. * //
    builder.addCase(
      updateNotificationPreference.rejected,
      (state, { meta }) => {
        const { type, channel, enabled } = meta.arg;
        state.preferences[type] = {
          ...(state.preferences[type] || {}),
          [channel]: !enabled,
        };
      }
    );

    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.notifications.forEach((item) => {
        item.read = true;
      });
      state.unreadCount = 0;
    });
  },
});

export default NotificationSlice.reducer;
export const { openNotifications, closeNotifications } =
  NotificationSlice.actions;
